import { memo, useState, useEffect } from "react";
import { useParticipantIds, useParticipantProperty } from "@daily-co/daily-react";
import { styled } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { motion } from "framer-motion";

const RemoteDisplayName = memo(function RemoteDisplayName({ 
  participantId 
}: { 
  participantId: string 
}) {
  const participantUserData = useParticipantProperty(participantId, "userData");
  const displayName = (participantUserData as any)?.displayName || "";
  
  return (
    <div className="display-name remote">
      {displayName}
    </div>
  );
});

export const RouletteDisplayNames = memo(function RouletteDisplayNames({
  isRouletteRoom,
  selfDisplayName,
  otherConnectionIds,
}: {
  isRouletteRoom: boolean;
  selfDisplayName: string;
  otherConnectionIds: number[];
}) {
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const { inDarkMode } = useSnapshot(localState);
  const [showNames, setShowNames] = useState(false);
  
  useEffect(() => {
    if (isRouletteRoom) {
      // Wait 2 seconds after entering roulette room before showing names
      const timer = setTimeout(() => {
        setShowNames(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      setShowNames(false);
    }
  }, [isRouletteRoom]);
  
  if (!isRouletteRoom || !showNames) return null;

  return (
    <StyledDisplayNamesContainer 
      Theme={inDarkMode ? "dark" : "light"}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="display-name self">
        {selfDisplayName || "you"}
      </div>
      
      {remoteParticipantIds.map((participantId) => (
        <RemoteDisplayName key={participantId} participantId={participantId} />
      ))}
      
      {otherConnectionIds.length === 0 && (
        <div className="display-name waiting">
          Finding  a match...
        </div>
      )}
    </StyledDisplayNamesContainer>
  );
});

const StyledDisplayNamesContainer = styled(motion.div, {
  position: "fixed",
  top: "calc(1rem + $navHeight + 7.4rem)", 
  left: "50%",
  transform: "translateX(-50%) scale(1.2) translateY(8px)", 
  display: "flex",
  gap: "1rem",
  "@mobile": {
    gap: "0.5rem",
  },
  justifyContent: "center",
  alignItems: "flex-start", // Add this to prevent stretching
  zIndex: "1",
  pointerEvents: "none",
  
  "& .display-name": {
    width: "6rem",
    textAlign: "center",
    fontFamily: "KeplerLightCondensed",
    fontSize: "13px",
    fontWeight: "400",
    color: "black",
    backgroundColor: "rgba(246, 246, 246, 0.3)",
    backdropFilter: "blur(5px)",
    borderRadius: "12px",
    padding: "4px 8px 1px 8px",
    whiteSpace: "normal",
    overflow: "hidden",
    lineHeight: "1.2",
    
    "@mobile": {
      fontSize: "15px",
    },
  },
  
  variants: {
    Theme: {
      light: {
        "& .display-name.waiting": {
          color: "#666",
        },
      },
      dark: {
        "& .display-name": {
          color: "white",
          backgroundColor: "rgba(27, 27, 27, 0.4)",
        },
        "& .display-name.waiting": {
          color: "#cccccc",
        },
      },
    },
  },
});
